import React, {
    useCallback,
    useContext,
    useMemo,
    useState,
} from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import PropTypes from "prop-types";
import {
    BufferService,
    LoadingContext,
    MButton,
    MMessageBoard,
    MTable,
} from "src/M_Components";
import {
    apiMessage,
    isFailurePayload,
} from "src/components/utils/apiResponse";
import "../../pages/config-page.css";

//region Constants

/**
 * Functional status values used by TenderExternalInterest.
 *
 * Important:
 * - the list must not show rejected interests;
 * - the BE filter expects the persisted enum numeric value, even if the list DTO
 *   later returns a translated status label.
 */
const TENDER_EXTERNAL_INTEREST_STATUS = {
    Rejected: 5,
};

/**
 * Supported decision actions for an external carrier interest.
 */
const INTEREST_DECISION_ACTION = {
    Accept: "accept",
    Reject: "reject",
};

//endregion

//region Helpers

/**
 * Reads the external-publication identifier from the publication payload.
 *
 * Purpose:
 * - supports both PascalCase and camelCase API payloads;
 * - keeps this component independent from TenderOpenPage helper functions;
 * - provides a stable identifier for the carrier-interests table filter.
 *
 * @param {object|null|undefined} publication - External publication payload.
 * @returns {number|string|null} Publication identifier or null when unavailable.
 */
const getExternalPublicationId = (publication) =>
    publication?.Id ?? publication?.id ?? null;

/**
 * Reads the external-interest identifier from the row payload.
 *
 * Purpose:
 * - supports both PascalCase and camelCase payloads;
 * - keeps decision calls stable even if the table payload casing changes.
 *
 * @param {object|null|undefined} interest - TenderExternalInterest row.
 * @returns {number|string|null} Interest identifier or null when unavailable.
 */
const getExternalInterestId = (interest) =>
    interest?.Id ?? interest?.id ?? null;

/**
 * Builds the fixed filter used by MTable to load external carrier interests.
 *
 * Business rules:
 * - only interests from the active external publication are shown;
 * - rejected interests are excluded from the operational review list.
 *
 * @param {number|string} publicationId - External publication identifier.
 * @returns {string} OData-like filter expression consumed by MTable.
 */
const buildCarrierInterestsFilter = (publicationId) =>
    `TenderExternalPublicationId eq ${publicationId} and InterestStatus ne ${TENDER_EXTERNAL_INTEREST_STATUS.Rejected}`;

/**
 * Builds the absolute decision endpoint URL.
 *
 * Purpose:
 * - avoids depending on BufferService.postQuery supporting body/config arguments;
 * - keeps this component copy-paste safe with the current BufferService version;
 * - still reuses the same base URL and entity name resolved by BufferService.
 *
 * @param {BufferService} service - TenderExternalInterests service instance.
 * @param {"accept"|"reject"} action - Decision action.
 * @param {number|string} interestId - Interest id.
 * @returns {string} Absolute endpoint URL.
 */
const buildDecisionUrl = (service, action, interestId) =>
    `${service.url}${service.entityName}/${action}/${interestId}`;

//endregion

//region Component

/**
 * Renders the external carrier interests workflow for an active external publication.
 *
 * Responsibilities:
 * - reuse MTable to render TenderExternalInterests;
 * - scope table data to the active external publication;
 * - exclude rejected interests from the list;
 * - keep the default MTable refresh behavior;
 * - expose Sync, Accept and Reject actions in the table header;
 * - expose Accept and Reject actions in the row context menu.
 *
 * Important:
 * - this component intentionally does not change MTable internals;
 * - default CRUD actions are disabled because these rows are workflow records;
 * - header Accept/Reject validates exactly one selected row;
 * - context-menu Accept/Reject acts on the clicked row.
 *
 * @param {object} props - Component props.
 * @param {object|null} props.publication - Active external publication DTO.
 * @returns {JSX.Element} Carrier interests view.
 */
export default function CarrierInterestsConfig({ publication }) {
    const { t } = useTranslation();
    const ctx = useContext(LoadingContext);

    const [refresh, setRefresh] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    const [decisionDialogOpen, setDecisionDialogOpen] = useState(false);
    const [decisionAction, setDecisionAction] = useState(null);
    const [decisionInterest, setDecisionInterest] = useState(null);
    const [decisionNotes, setDecisionNotes] = useState("");

    /**
     * Returns a translated value when available, otherwise returns the fallback.
     *
     * Purpose:
     * - allows the component to work before all translation keys are registered;
     * - keeps labels stable across Portuguese, English, Spanish, or future languages.
     *
     * @param {string} key - Translation key.
     * @param {string} fallback - Fallback label.
     * @returns {string} Translated or fallback label.
     */
    const tOrLocal = useCallback(
        (key, fallback) => {
            const value = t(key);
            return value && value !== key ? value : fallback;
        },
        [t]
    );

    const publicationId = getExternalPublicationId(publication);

    const interestsFilter = useMemo(() => {
        if (publicationId == null) return null;

        return buildCarrierInterestsFilter(publicationId);
    }, [publicationId]);

    const interestsService = useMemo(
        () => new BufferService("TenderExternalInterests", ctx.DEFAULTURL),
        [ctx.DEFAULTURL]
    );

    /**
     * Refreshes the MTable data after a workflow action.
     *
     * @returns {void}
     */
    const refreshTable = useCallback(() => {
        setRefresh(true);
    }, []);

    /**
     * Synchronizes external carrier interests for the active publication.
     *
     * Flow:
     * - calls the publication-scoped sync endpoint;
     * - shows a normalized API error when the request fails;
     * - refreshes the MTable after a successful sync.
     *
     * @returns {Promise<void>}
     */
    const syncExternalInterests = useCallback(async () => {
        if (publicationId == null) {
            ctx.toastMessages?.(
                "error",
                "",
                tOrLocal(
                    "ExternalPublicationNotAvailable",
                    "External publication is not available."
                )
            );
            return;
        }

        ctx.setIsLoad?.(true);

        try {
            const res = await interestsService.postQuery(
                `/sync/${publicationId}`
            );

            if (isFailurePayload(res)) {
                ctx.toastMessages?.(
                    "error",
                    "",
                    apiMessage(
                        res,
                        tOrLocal(
                            "ErrorSyncingCarrierInterests",
                            "Error syncing carrier interests."
                        )
                    )
                );
                return;
            }

            ctx.toastMessages?.(
                "success",
                "",
                tOrLocal(
                    "CarrierInterestsSynced",
                    "Carrier interests synchronized successfully."
                )
            );

            refreshTable();
        } catch (err) {
            ctx.toastMessages?.(
                "error",
                "",
                apiMessage(
                    err,
                    tOrLocal(
                        "ErrorSyncingCarrierInterests",
                        "Error syncing carrier interests."
                    )
                )
            );
        } finally {
            ctx.setIsLoad?.(false);
        }
    }, [
        publicationId,
        interestsService,
        ctx,
        tOrLocal,
        refreshTable,
    ]);

    /**
     * Resolves exactly one selected row for header decision actions.
     *
     * Important:
     * - MTable may still keep selection as an array even in single-select mode;
     * - the business rule requires exactly one selected interest.
     *
     * @returns {object|null} Selected interest row or null when invalid.
     */
    const getSingleSelectedInterest = useCallback(() => {
        const rows = Array.isArray(selectedRows) ? selectedRows : [];

        if (rows.length !== 1) {
            ctx.toastMessages?.(
                "error",
                "",
                tOrLocal(
                    "SelectExactlyOneInterest",
                    "Select exactly one carrier interest."
                )
            );
            return null;
        }

        return rows[0];
    }, [selectedRows, ctx, tOrLocal]);

    /**
     * Opens the decision dialog for a specific interest row.
     *
     * @param {"accept"|"reject"} action - Decision action.
     * @param {object|null|undefined} interest - Interest row.
     * @returns {void}
     */
    const openDecisionDialog = useCallback(
        (action, interest) => {
            if (!interest) {
                ctx.toastMessages?.(
                    "error",
                    "",
                    tOrLocal("SelectedLine", "Select a row.")
                );
                return;
            }

            const interestId = getExternalInterestId(interest);

            if (interestId == null) {
                ctx.toastMessages?.(
                    "error",
                    "",
                    tOrLocal(
                        "InvalidCarrierInterest",
                        "The selected carrier interest is invalid."
                    )
                );
                return;
            }

            setDecisionAction(action);
            setDecisionInterest(interest);
            setDecisionNotes("");
            setDecisionDialogOpen(true);
        },
        [ctx, tOrLocal]
    );

    /**
     * Opens the decision dialog from the table header actions.
     *
     * @param {"accept"|"reject"} action - Decision action.
     * @returns {void}
     */
    const openDecisionDialogFromSelection = useCallback(
        (action) => {
            const selectedInterest = getSingleSelectedInterest();
            if (!selectedInterest) return;

            openDecisionDialog(action, selectedInterest);
        },
        [getSingleSelectedInterest, openDecisionDialog]
    );

    /**
     * Closes and resets the decision dialog.
     *
     * @returns {void}
     */
    const closeDecisionDialog = useCallback(() => {
        setDecisionDialogOpen(false);
        setDecisionAction(null);
        setDecisionInterest(null);
        setDecisionNotes("");
    }, []);

    /**
     * Confirms an accept/reject decision for the selected external interest.
     *
     * Endpoint contract:
     * - POST TenderExternalInterests/accept/{id}
     * - POST TenderExternalInterests/reject/{id}
     * - Content-Type: application/json-patch+json
     * - body: { decisionNotes: string }
     *
     * Important:
     * - axios is used directly here because the current BufferService.postQuery
     *   only accepts a URL suffix and does not forward body/config arguments;
     * - this prevents the 415 Unsupported Media Type error from the backend.
     *
     * @returns {Promise<void>}
     */
    const confirmDecision = useCallback(async () => {
        const interestId = getExternalInterestId(decisionInterest);

        if (!decisionAction || interestId == null) {
            closeDecisionDialog();
            return;
        }

        ctx.setIsLoad?.(true);

        try {
            await axios.post(
                buildDecisionUrl(interestsService, decisionAction, interestId),
                {
                    decisionNotes: decisionNotes || "",
                },
                {
                    headers: {
                        Accept: "text/plain",
                        "Content-Type": "application/json-patch+json",
                    },
                }
            );

            ctx.toastMessages?.(
                "success",
                "",
                decisionAction === INTEREST_DECISION_ACTION.Accept
                    ? tOrLocal(
                        "CarrierInterestAccepted",
                        "Carrier interest accepted successfully."
                    )
                    : tOrLocal(
                        "CarrierInterestRejected",
                        "Carrier interest rejected successfully."
                    )
            );

            closeDecisionDialog();
            setSelectedRows([]);
            refreshTable();
        } catch (err) {
            ctx.toastMessages?.(
                "error",
                "",
                apiMessage(
                    err,
                    decisionAction === INTEREST_DECISION_ACTION.Accept
                        ? tOrLocal(
                            "ErrorAcceptingCarrierInterest",
                            "Error accepting carrier interest."
                        )
                        : tOrLocal(
                            "ErrorRejectingCarrierInterest",
                            "Error rejecting carrier interest."
                        )
                )
            );
        } finally {
            ctx.setIsLoad?.(false);
        }
    }, [
        decisionAction,
        decisionInterest,
        decisionNotes,
        interestsService,
        ctx,
        closeDecisionDialog,
        refreshTable,
        tOrLocal,
    ]);

    /**
     * Extra MTable header actions.
     *
     * Important:
     * - Refresh is already provided by MTable;
     * - Sync acts on the publication and does not need a selected row;
     * - Accept/Reject use selectedRows state to enforce exactly one selected row.
     */
    const extraButtons = useMemo(
        () => [
            {
                element: "default",
                label: tOrLocal("Sync", "Sync"),
                needLine: false,
                icon: (
                    <i
                        className="pi pi-sync iconTable"
                        style={{ color: "var(--color-blue)" }}
                    />
                ),
                command: syncExternalInterests,
                extraEl: null,
            },
            {
                element: "default",
                label: tOrLocal("Accept", "Accept"),
                needLine: false,
                icon: (
                    <i
                        className="pi pi-check iconTable"
                        style={{ color: "var(--color-green-light)" }}
                    />
                ),
                command: () =>
                    openDecisionDialogFromSelection(
                        INTEREST_DECISION_ACTION.Accept
                    ),
                extraEl: null,
            },
            {
                element: "default",
                label: tOrLocal("Reject", "Reject"),
                needLine: false,
                icon: (
                    <i
                        className="pi pi-times iconTable"
                        style={{ color: "var(--color-red)" }}
                    />
                ),
                command: () =>
                    openDecisionDialogFromSelection(
                        INTEREST_DECISION_ACTION.Reject
                    ),
                extraEl: null,
            },
        ],
        [
            syncExternalInterests,
            openDecisionDialogFromSelection,
            tOrLocal,
        ]
    );

    /**
     * MTable row context-menu actions.
     *
     * Important:
     * - these actions receive the row from the right-click context;
     * - therefore no selected-row validation is needed here.
     */
    const lineContext = useMemo(
        () => [
            {
                label: tOrLocal("Accept", "Accept"),
                icon: (
                    <i
                        className="pi pi-check"
                        style={{
                            color: "var(--color-green-light)",
                            paddingRight: "5px",
                        }}
                    />
                ),
                command: (row) =>
                    openDecisionDialog(
                        INTEREST_DECISION_ACTION.Accept,
                        row
                    ),
            },
            {
                label: tOrLocal("Reject", "Reject"),
                icon: (
                    <i
                        className="pi pi-times"
                        style={{
                            color: "var(--color-red)",
                            paddingRight: "5px",
                        }}
                    />
                ),
                command: (row) =>
                    openDecisionDialog(
                        INTEREST_DECISION_ACTION.Reject,
                        row
                    ),
            },
        ],
        [openDecisionDialog, tOrLocal]
    );

    const decisionTitle =
        decisionAction === INTEREST_DECISION_ACTION.Accept
            ? tOrLocal("AcceptCarrierInterest", "Accept carrier interest")
            : tOrLocal("RejectCarrierInterest", "Reject carrier interest");

    const decisionMessage =
        decisionAction === INTEREST_DECISION_ACTION.Accept
            ? tOrLocal(
                "ConfirmAcceptCarrierInterest",
                "Are you sure you want to accept this carrier interest?"
            )
            : tOrLocal(
                "ConfirmRejectCarrierInterest",
                "Are you sure you want to reject this carrier interest?"
            );

    if (publicationId == null || !interestsFilter) {
        return (
            <div className="config-page config-page--no-padding">
                <div className="config-page__header">
                    <div className="config-page__heading">
                        <div
                            className="config-page__title"
                            style={{ color: ctx.secondaryColor }}
                        >
                            {tOrLocal("CarrierInterests", "Carrier Interests")}
                        </div>

                        <div className="config-page__subtitle">
                            {tOrLocal(
                                "CarrierInterestsPublicationMissingInfo",
                                "Carrier interests cannot be loaded because there is no active external publication for the current target."
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="config-page config-page--no-padding">
            <div className="config-page__header">
                <div className="config-page__heading">
                    <div
                        className="config-page__title"
                        style={{ color: ctx.secondaryColor }}
                    >
                        {tOrLocal("CarrierInterests", "Carrier Interests")}
                    </div>

                    <div className="config-page__subtitle">
                        {tOrLocal(
                            "CarrierInterestsDescription",
                            "Review the external carrier interests received for the active publication. You can synchronize new interests, accept valid carrier interests, or reject interests that should no longer be considered."
                        )}
                    </div>
                </div>
            </div>

            <div className="config-page__table-scroll">
                <div className="config-page__table-inner">
                    <MTable
                        entityName="TenderExternalInterests"
                        roleName="TenderExternalInterests"
                        manualId={`TenderExternalInterests:${publicationId}`}
                        useSessionStorage={false}
                        crud={false}
                        selectSingle
                        filtersOnTable
                        inputFilter={false}
                        maxRows={20}
                        tableHeight="55vh"
                        refresh={refresh}
                        setRefresh={setRefresh}
                        FILTER={interestsFilter}
                        extraButtons={extraButtons}
                        lineContext={lineContext}
                        onPassLine={setSelectedRows}
                        removeContextItems={[
                            "View",
                            "Create",
                            "Update",
                            "Delete",
                            "Copy",
                            "Audit",
                        ]}
                        hideDefaultColor
                    />
                </div>
            </div>

            <MMessageBoard
                title={decisionTitle}
                open={decisionDialogOpen}
                onClose={closeDecisionDialog}
                warning
                body={
                    <div>
                        <div style={{ marginBottom: "12px" }}>
                            {decisionMessage}
                        </div>

                        <label
                            htmlFor="carrier-interest-decision-notes"
                            className="config-page__subtitle"
                            style={{
                                display: "block",
                                marginBottom: "6px",
                            }}
                        >
                            {tOrLocal("DecisionNotes", "Decision notes")}
                        </label>

                        <textarea
                            id="carrier-interest-decision-notes"
                            value={decisionNotes}
                            onChange={(event) =>
                                setDecisionNotes(event.target.value)
                            }
                            rows={4}
                            style={{
                                width: "100%",
                                minHeight: "90px",
                                resize: "vertical",
                                border: "1px solid var(--color-grey-light)",
                                borderRadius: "6px",
                                padding: "8px",
                                fontFamily: "inherit",
                            }}
                        />
                    </div>
                }
                footer={
                    <>
                        <MButton
                            label={tOrLocal("Cancel", "Cancel")}
                            backgroundColor={"var(--color-red)"}
                            labelColor={"var(--color-white)"}
                            icon={
                                <i
                                    className="pi pi-times iconTable"
                                    style={{ color: "white" }}
                                />
                            }
                            onClick={closeDecisionDialog}
                        />

                        <MButton
                            label={tOrLocal("Confirm", "Confirm")}
                            backgroundColor={"var(--color-green)"}
                            labelColor={"var(--color-white)"}
                            icon={
                                <i
                                    className="pi pi-check iconTable"
                                    style={{ color: "white" }}
                                />
                            }
                            onClick={confirmDecision}
                        />
                    </>
                }
            />
        </div>
    );
}

//endregion

//region PropTypes

CarrierInterestsConfig.propTypes = {
    publication: PropTypes.object,
    tenderInfo: PropTypes.object,
    isSpotTender: PropTypes.bool,
    selectedPhaseId: PropTypes.oneOfType([
        PropTypes.number,
        PropTypes.string,
    ]),
};

//endregion